import { Switch } from '@headlessui/react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

import useMe from '@/renderer/utils/useMe';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

type UpdateData = { free: boolean };

// API requests
const updateFreeRequest: RequestSchema<API.User, API.WithData<UpdateData>> = {
  name: 'updateFreeRequest',
  input: '/api/v1/user/update/me?v2Verify=true',
  method: 'POST',
};

export default function FreePlaysSwitch({ defaultValue }: { defaultValue: boolean }) {
  const [, setMe] = useMe(true);
  const [enabled, setEnabled] = useState(defaultValue);

  const request = useRequest(updateFreeRequest, { manual: true });

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [isSubmitted]);

  const handleChange = async (free: boolean) => {
    const result = await request.run({ data: { free } }, { throwOnRun: true });

    setEnabled(free);

    if (result) {
      setIsSubmitted(true);
      setMe(result);
    }
  };

  return (
    <div className="flex items-start space-x-4">
      <Switch
        checked={enabled}
        onChange={handleChange}
        className={clsx(
          enabled ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-zinc-200 dark:bg-zinc-700',
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full',
          request.loading && 'animate-pulse',
        )}
        disabled={request.loading}
      >
        <span className="sr-only">Free plays status switch</span>
        <span
          className={`${
            enabled ? 'translate-x-6' : 'translate-x-1'
          } inline-block size-4 rounded-full bg-white transition`}
        />
      </Switch>
      <div className="flex flex-col space-y-1">
        <div className="text-sm text-zinc-500 dark:text-zinc-400">
          Includes free plays, service announcements and other promotions.
        </div>
        <div
          className={clsx(
            'text-emerald-600 text-xs leading-4 transition-opacity duration-500 dark:text-emerald-400',
            isSubmitted ? 'opacity-100' : 'opacity-0',
          )}
        >
          {enabled ? 'Subscribed!' : 'Unsubscribed!'}
        </div>
      </div>
    </div>
  );
}
